<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ContractGenerationQuestionType, type ContractGenerationTemplateQuestionInDb } from '@/api';
import {
  QUESTION_TYPE_HINT,
  QUESTION_TYPE_LABEL,
  describeBounds,
  type QuestionBounds,
} from '@/utils/contractGeneration/questions';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSegmented from '@/components/design/base/GaiaSegmented.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaDateField from '@/components/design/base/inputs/GaiaDateField.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';

/**
 * One question a template asks. The type decides how every document answers
 * it — a fixed choice, a band of numbers, or a span of dates — which is why it
 * is settled at creation and locked afterwards: every answer already given is
 * stored in that shape.
 *
 * A choice question states no answers here. Its admissible values are derived
 * from what the documents say: one answers "male", and "male" is thereafter in
 * the picker. That keeps the two from ever disagreeing — there is no authored
 * list for a document's answer to fall outside of.
 *
 * An ordered question may instead declare the range it is about — inclusive at
 * both ends, either end optional. Like the type, it is settled here and never
 * moves: the update route has skip-None semantics, so there would be no way to
 * say "remove the upper bound", and moving one would have to answer for the
 * answers already outside it.
 */
interface Props {
  show: boolean;
  /** Null adds a question; a question edits that one. */
  question?: ContractGenerationTemplateQuestionInDb | null;
  /** Labels already on this template — a duplicate is refused by the backend. */
  existingLabels?: string[];
  saving?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  question: null,
  existingLabels: () => [],
  saving: false,
});

export interface QuestionEditorSubmit {
  label: string;
  hint: string | null;
  question_type: ContractGenerationQuestionType;
  required_for_variants: boolean;
  /**
   * The inclusive range it covers — one pair, whichever type this is. Null for
   * a choice question and for an ordered one left unbounded. Which of the
   * request's two field pairs it becomes is `boundsPayload`'s business.
   */
  bounds: QuestionBounds | null;
}

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'submit', value: QuestionEditorSubmit): void;
}>();

const label = ref<string>('');
const hint = ref<string>('');
const questionType = ref<ContractGenerationQuestionType>(ContractGenerationQuestionType.SELECTION);
const requiredForVariants = ref<boolean>(false);
/** Optional limits on what any later answer — or drafting value — may name. */
const numberMin = ref<string>('');
const numberMax = ref<string>('');
const dateMin = ref<string | null>(null);
const dateMax = ref<string | null>(null);

function reset(): void {
  label.value = props.question?.label ?? '';
  hint.value = props.question?.hint ?? '';
  questionType.value = props.question?.question_type ?? ContractGenerationQuestionType.SELECTION;
  requiredForVariants.value = props.question?.required_for_variants ?? false;
  numberMin.value = '';
  numberMax.value = '';
  dateMin.value = null;
  dateMax.value = null;
}

const isOrdered = computed<boolean>(
  () => questionType.value !== ContractGenerationQuestionType.SELECTION
);

const isDateQuestion = computed<boolean>(
  () => questionType.value === ContractGenerationQuestionType.DATE
);

/**
 * Inclusive at both ends, so an equal pair is a question about exactly one
 * value — odd, but not wrong, and the backend allows it.
 */
const boundsError = computed<string | null>(() => {
  if (!isOrdered.value) return null;
  if (isDateQuestion.value) {
    if (dateMin.value !== null && dateMax.value !== null && dateMin.value > dateMax.value) {
      return 'The lower bound has to fall on or before the upper one.';
    }
    return null;
  }
  const min = numberMin.value.trim();
  const max = numberMax.value.trim();
  if (min.length > 0 && !Number.isFinite(Number(min))) return 'The lower bound isn’t a number.';
  if (max.length > 0 && !Number.isFinite(Number(max))) return 'The upper bound isn’t a number.';
  if (min.length > 0 && max.length > 0 && Number(min) > Number(max)) {
    return 'The lower bound has to be at or below the upper one.';
  }
  return null;
});

watch(
  () => props.show,
  show => {
    if (show) reset();
  },
  { immediate: true }
);

const isEditing = computed<boolean>(() => props.question !== null);

const typeOptions = [
  ContractGenerationQuestionType.SELECTION,
  ContractGenerationQuestionType.NUMBER,
  ContractGenerationQuestionType.DATE,
].map(type => ({ label: QUESTION_TYPE_LABEL[type], value: type }));

const labelTaken = computed<boolean>(() => {
  const trimmed = label.value.trim().toLowerCase();
  if (trimmed.length === 0) return false;
  if (props.question && props.question.label.toLowerCase() === trimmed) return false;
  return props.existingLabels.some(existing => existing.toLowerCase() === trimmed);
});

const canSubmit = computed<boolean>(
  () =>
    !props.saving &&
    label.value.trim().length > 0 &&
    !labelTaken.value &&
    boundsError.value === null
);

function submit(): void {
  if (!canSubmit.value) return;
  emit('submit', {
    label: label.value.trim(),
    hint: hint.value.trim() || null,
    question_type: questionType.value,
    required_for_variants: requiredForVariants.value,
    bounds: isOrdered.value
      ? isDateQuestion.value
        ? { min: dateMin.value, max: dateMax.value }
        : { min: numberMin.value.trim() || null, max: numberMax.value.trim() || null }
      : null,
  });
}
</script>

<template>
  <gaia-modal
    :show="props.show"
    width="500px"
    :mask-closable="!props.saving"
    :closable="!props.saving"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <template #header>
      <n-flex vertical :size="6">
        <gaia-text size="subtitle" weight="semibold" tone="ink">
          {{ isEditing ? 'Edit question' : 'Add question' }}
        </gaia-text>
        <gaia-text size="row" tone="text">
          Something this template varies by. Whoever drafts answers it, and the answer narrows which
          document they get.
        </gaia-text>
      </n-flex>
    </template>

    <n-flex vertical :size="14">
      <gaia-form-field label="Question" required hint="what it varies by">
        <gaia-input
          v-model:value="label"
          placeholder="e.g. Language, Gender, Annual salary"
          :disabled="props.saving"
          style="width: 100%"
        />
      </gaia-form-field>

      <gaia-banner v-if="labelTaken" type="warning">
        This template already asks a question by that name.
      </gaia-banner>

      <gaia-form-field label="Hint" hint="shown under the field while drafting">
        <gaia-textarea
          v-model:value="hint"
          placeholder="e.g. The employee’s gross annual salary, before bonuses."
          :rows="2"
          :disabled="props.saving"
          style="width: 100%"
        />
      </gaia-form-field>

      <!-- Immutable after creation: every answer already given is stored in the
           shape this chooses, so changing it later would strand all of them. -->
      <gaia-form-field label="Answer type" :required="!isEditing">
        <n-flex v-if="isEditing" align="center" :size="8">
          <gaia-tag size="small" round>{{ QUESTION_TYPE_LABEL[questionType] }}</gaia-tag>
          <gaia-text size="meta" tone="subtle">
            can’t change — every answer already given is in this shape
          </gaia-text>
        </n-flex>
        <n-flex v-else vertical :size="6">
          <gaia-segmented
            :value="questionType"
            :options="typeOptions"
            size="medium"
            block
            :disabled="props.saving"
            @update:value="
              (value: string) => (questionType = value as ContractGenerationQuestionType)
            "
          />
          <gaia-text size="meta" tone="subtle">{{ QUESTION_TYPE_HINT[questionType] }}</gaia-text>
        </n-flex>
      </gaia-form-field>

      <!-- The range the question is about, if it has one. Inclusive at both
           ends — unlike the "between" a document answers with, whose upper
           value is excluded so that adjacent bands meet exactly once. Settled
           here because it never moves afterwards. -->
      <gaia-form-field
        v-if="isOrdered && !isEditing"
        label="Range it covers"
        hint="optional — limits every later answer and every drafting value"
        :error="boundsError ?? undefined"
      >
        <n-flex align="flex-start" :size="8" :wrap="false">
          <template v-if="isDateQuestion">
            <gaia-date-field
              :value="dateMin"
              placeholder="No lower limit"
              :disabled="props.saving"
              :style="{ flex: '1 1 0', minWidth: 0 }"
              @update:value="(value: string | null) => (dateMin = value)"
            />
            <gaia-text
              size="meta"
              tone="subtle"
              :style="{ flex: 'none', whiteSpace: 'nowrap', paddingTop: '9px' }"
            >
              to
            </gaia-text>
            <gaia-date-field
              :value="dateMax"
              placeholder="No upper limit"
              :disabled="props.saving"
              :style="{ flex: '1 1 0', minWidth: 0 }"
              @update:value="(value: string | null) => (dateMax = value)"
            />
          </template>
          <template v-else>
            <gaia-input
              v-model:value="numberMin"
              placeholder="No lower limit"
              :disabled="props.saving"
              :style="{ flex: '1 1 0', minWidth: 0 }"
            />
            <gaia-text
              size="meta"
              tone="subtle"
              :style="{ flex: 'none', whiteSpace: 'nowrap', paddingTop: '9px' }"
            >
              to
            </gaia-text>
            <gaia-input
              v-model:value="numberMax"
              placeholder="No upper limit"
              :disabled="props.saving"
              :style="{ flex: '1 1 0', minWidth: 0 }"
            />
          </template>
        </n-flex>
      </gaia-form-field>

      <!-- Immutable like the type, so an existing question shows it and can't
           change it. -->
      <gaia-form-field
        v-else-if="isOrdered && describeBounds(props.question!)"
        label="Range it covers"
      >
        <n-flex align="center" :size="8">
          <gaia-tag size="small" round>{{ describeBounds(props.question!) }}</gaia-tag>
          <gaia-text size="meta" tone="subtle">
            can’t change — answers already sit inside it
          </gaia-text>
        </n-flex>
      </gaia-form-field>

      <!-- The one setting that can take a template out of service, so it says
           so in its own words rather than leaving the author to find out. -->
      <gaia-toggle-row
        card
        label="Every document must answer this"
        :hint="
          requiredForVariants
            ? 'The template can’t be drafted from until every document answers it.'
            : 'Documents that don’t answer it simply aren’t narrowed by it.'
        "
        :checked="requiredForVariants"
        :disabled="props.saving"
        @update:checked="(value: boolean) => (requiredForVariants = value)"
      />
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button size="small" :disabled="props.saving" @click="emit('update:show', false)">
          Cancel
        </gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="props.saving"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ isEditing ? 'Save question' : 'Add question' }}
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
